/**
 * Apagar linhas do chat da mesa (docs/spec/chat-delete.md): que linha um mestre pode apagar, o que a
 * pergunta mostra e o modo /audit, em que as apagadas voltam ao lugar delas, marcadas.
 */
import { parseTalemark, talemarkToPlainText, type ChatMessage, type DeletedChatLine } from '../shared.js';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** Até onde a pergunta mostra a mensagem. */
const EXCERPT = 120;

/** As que alguém mandou no chat da mesa: as do sistema (e as locais, que também são do sistema) e as privadas não. */
export function canDeleteLine(m: ChatMessage): boolean {
  return !m.channel && m.kind !== 'system';
}

/** Quem aparece na linha: o narrador, o NPC, o apelido ou o nome. */
export function lineAuthor(t: Translate, m: ChatMessage): string {
  if (m.persona?.kind === 'narrator') return t('chat.persona.narrator');
  if (m.persona?.kind === 'npc') return m.persona.name;
  return m.nick ?? m.displayName;
}

/** O texto da linha sem a formatação (para copiar e para a pergunta); o arquivo sem legenda, pelo nome. */
export function lineText(m: ChatMessage): string {
  if (m.kind === 'roll') return m.text;
  return talemarkToPlainText(parseTalemark(m.text)).trim();
}

/** A pergunta antes de apagar: "Apagar a mensagem de Bob?", com um trecho dela, e o que acontece. */
export function deletePrompt(t: Translate, m: ChatMessage): { title: string; message: string } {
  const text = lineText(m);
  const excerpt = text.length > EXCERPT ? `${text.slice(0, EXCERPT).trimEnd()}…` : text;
  return {
    title: t(m.kind === 'roll' ? 'chat.delete.roll.title' : 'chat.delete.title', { name: lineAuthor(t, m) }),
    message: `${excerpt ? `“${excerpt}”\n\n` : ''}${t('chat.delete.message')}`,
  };
}

/** Uma linha do log no modo /audit: a mensagem e, se foi apagada, quem apagou e quando. */
export type AuditedMessage = { m: ChatMessage; deleted?: DeletedChatLine };

/**
 * As mensagens do chat com as apagadas no lugar delas, pela hora. Uma apagada que ainda está na tela
 * (o aviso de que saiu ainda não chegou) aparece uma vez só, como apagada.
 */
export function withDeleted(messages: readonly ChatMessage[], deleted: readonly DeletedChatLine[]): AuditedMessage[] {
  const ids = new Set(deleted.map((d) => d.message.id));
  return [...messages.filter((m) => !ids.has(m.id)).map((m) => ({ m })), ...deleted.map((d) => ({ m: d.message, deleted: d }))].sort(
    (a, b) => a.m.createdAt - b.m.createdAt,
  );
}

/** A dica da marca "(apagada)": quem apagou e quando ("ao limpar o chat", se foi assim). `when` formata a hora. */
export function deletedHint(t: Translate, d: DeletedChatLine, when: (ms: number) => string): string {
  return t(d.cleared ? 'chat.deleted.byClear' : 'chat.deleted.by', { name: d.deletedByName, when: when(d.deletedAt) });
}
